import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import AdminLayout from '@/components/admin/AdminLayout';
import AdminLogin from '@/components/admin/AdminLogin';
import Dashboard from '@/components/admin/Dashboard';
import BookingRequests from '@/components/admin/BookingRequests';
import Bookings from '@/components/admin/Bookings';
import Properties from '@/components/admin/Properties';
import Rates from '@/components/admin/Rates';
import Services from '@/components/admin/Services';
import Extras from '@/components/admin/Extras';
import Devices from '@/components/admin/Devices';
import Integrations from '@/components/admin/Integrations';
import Guests from '@/components/admin/Guests';
import AdminUsers from '@/components/admin/AdminUsers';
import AccessLog from '@/components/admin/AccessLog';
import Settings from '@/components/admin/Settings';
import Suppliers from '@/components/admin/Suppliers';
import Translations from '@/components/admin/Translations';
import { FeatureGate } from '@/components/admin/FeatureGate';
import { ApiKeys } from '@/components/admin/ApiKeys';
import { Webhooks } from '@/components/admin/Webhooks';
import { WebhookAnalytics } from '@/components/admin/WebhookAnalytics';
import { Guide } from '@/components/admin/Guide';
import { MessageTemplates } from '@/components/admin/MessageTemplates';
import { MarketplacePanel } from '@/components/admin/MarketplacePanel';
import { BrandingPage } from '@/components/admin/BrandingPage';
import { ReceiptsPage } from '@/components/admin/ReceiptsPage';
import UsefulLinks from '@/components/admin/UsefulLinks';

// Wrapper component per ApiKeys
function ApiKeysSection() {
  const token = localStorage.getItem('admin_token') || '';
  const { slug, tenant } = useTenant();
  const tenantSlug = slug || new URLSearchParams(window.location.search).get('tenant') || '';
  const tenantPlan = tenant?.plan || 'essenziale';
  return (
    <FeatureGate feature="api_access">
      <ApiKeys token={token} tenantSlug={tenantSlug} tenantPlan={tenantPlan} />
    </FeatureGate>
  );
}

function WebhooksSection() {
  const token = localStorage.getItem('admin_token') || '';
  const { slug } = useTenant();
  const tenantSlug = slug || new URLSearchParams(window.location.search).get('tenant') || '';
  return (
    <FeatureGate feature="api_access">
      <Webhooks token={token} tenantSlug={tenantSlug} />
    </FeatureGate>
  );
}

function WebhookAnalyticsSection() {
  const token = localStorage.getItem('admin_token') || '';
  const { slug } = useTenant();
  const tenantSlug = slug || new URLSearchParams(window.location.search).get('tenant') || '';
  return (
    <FeatureGate feature="api_access">
      <WebhookAnalytics token={token} tenantSlug={tenantSlug} />
    </FeatureGate>
  );
}

function GuideSection() {
  const token = localStorage.getItem('admin_token') || '';
  const { slug } = useTenant();
  const tenantSlug = slug || new URLSearchParams(window.location.search).get('tenant') || '';
  return <Guide onNavigate={(id) => { window.dispatchEvent(new CustomEvent('admin-navigate', { detail: id })); }} token={token} tenantSlug={tenantSlug} />;
}

// Mappa sezione → { component, feature (opzionale) }
const SECTIONS = {
  'dashboard':       { component: Dashboard },
  'booking-requests':{ component: BookingRequests },
  'bookings':        { component: Bookings },
  'guests':          { component: Guests },
  'properties':      { component: Properties },
  'rates':           { component: Rates },
  'services':        { component: Services },
  'extras':          { component: Extras },
  'suppliers':       { component: Suppliers,    feature: 'supplier_portal' },
  'integrations':    { component: Integrations, feature: 'smart_home' },
  'devices':         { component: Devices,      feature: 'smart_home' },
  'access-log':      { component: AccessLog,    feature: 'access_log' },
  'admin-users':     { component: AdminUsers },
  'translations':    { component: Translations, feature: 'i18n_editor' },
  'settings':        { component: Settings },
  'api-keys':        { component: ApiKeysSection, feature: 'api_access' },
  'webhooks':        { component: WebhooksSection, feature: 'api_access' },
  'webhook-analytics': { component: WebhookAnalyticsSection, feature: 'api_access' },
  'guide':              { component: GuideSection },
  'message-templates':  { component: MessageTemplates },
  'marketplace-panel':  { component: MarketplacePanel },
  'branding':           { component: BrandingPage, feature: 'white_label' },
  'receipts':           { component: ReceiptsPage },
  'useful-links':       { component: UsefulLinks },
};

export default function AdminPanel() {
  const { user, loading } = useAuth();
  // Legge l'hash prima del primo render per deep-link diretto
  const [section, setSection] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return (hash && SECTIONS[hash]) ? hash : 'dashboard';
  });

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && SECTIONS[hash]) {
      setSection(hash);
    }
  }, []);

  const navigateTo = (sec) => {
    setSection(sec);
    window.location.hash = sec;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={() => {}} />;
  }

  const sectionDef = SECTIONS[section] || SECTIONS['dashboard'];
  const SectionComponent = sectionDef.component;

  const content = sectionDef.feature ? (
    <FeatureGate feature={sectionDef.feature}>
      <SectionComponent key={section} section={section} onNavigate={navigateTo} />
    </FeatureGate>
  ) : (
    <SectionComponent key={section} section={section} onNavigate={navigateTo} />
  );

  return (
    <AdminLayout section={section} onNavigate={navigateTo}>
      {content}
    </AdminLayout>
  );
}
